/**
 * Optional cache codec, not a calculation worker. One worker per tab, at most
 * four outstanding jobs, released after idle. JSON never falls back to the
 * UI thread: unsupported/blocked/crashed worker means an ordinary cache miss.
 * Models are cloned in acknowledged batches with a UI yield between them.
 * postMessage is still not zero-cost; a single exceptionally large model can
 * still block, but the whole snapshot is never cloned in one UI task.
 * The static, versioned public script needs no Next worker bundling or Blob URL
 * (and works with worker-src 'self'). Increment its filename on protocol changes.
 */
type Job = {
  id: number;
  operation: "encode" | "decode";
  value: unknown;
  resolve: (value: unknown | null) => void;
  timer: ReturnType<typeof setTimeout>;
  models?: unknown[];
  offset?: number;
  decoded?: { models: unknown[]; [key: string]: unknown };
};

const MODEL_BATCH_SIZE = 8; // Must match the versioned worker's decode batches.

let worker: Worker | undefined;
let unavailable = false;
let sequence = 0;
let active: Job | undefined;
let queue: Job[] = [];
let idle: ReturnType<typeof setTimeout> | undefined;
let step: ReturnType<typeof setTimeout> | undefined;

/** Also revokes in-flight jobs when logging out; a later account can start anew. */
export function stopSupplyPlanJsonCodec(): void {
  if (idle) clearTimeout(idle);
  idle = undefined;
  if (step) clearTimeout(step);
  step = undefined;
  if (worker) {
    worker.onmessage = worker.onerror = worker.onmessageerror = null;
    worker.terminate();
  }
  worker = undefined;
  for (const job of [...(active ? [active] : []), ...queue]) {
    clearTimeout(job.timer);
    job.resolve(null);
  }
  active = undefined;
  queue = [];
}

function finish(value: unknown | null): void {
  const job = active;
  if (!job) return;
  active = undefined;
  if (step) clearTimeout(step);
  step = undefined;
  clearTimeout(job.timer);
  job.resolve(value);
  if (queue.length) pump();
  else idle = setTimeout(stopSupplyPlanJsonCodec, 1_000);
}

function send(message: object): void {
  try { worker!.postMessage(message); }
  catch { finish(null); }
}

/** A macrotask, not a microtask: let input/painting run between model batches. */
function nextBatch(job: Job): void {
  step = setTimeout(() => {
    step = undefined;
    if (active !== job) return;
    if (job.operation === "decode") send({ id: job.id, operation: "next" });
    else {
      const offset = job.offset ?? 0;
      if (offset < job.models!.length) {
        const value = job.models!.slice(offset, offset + MODEL_BATCH_SIZE);
        job.offset = offset + value.length;
        send({ id: job.id, operation: "models", value });
      } else {
        job.models = undefined;
        send({ id: job.id, operation: "finish" });
      }
    }
  }, 0);
}

function pump(): void {
  if (active || !queue.length) return;
  if (!worker) {
    try {
      worker = new Worker("/workers/supply-plan-json-v1.js");
      worker.onmessage = ({ data }: MessageEvent<{ id: number; phase: string; value?: unknown }>) => {
        if (!active || data?.id !== active.id) return;
        const job = active;
        if (data.phase === "complete") finish(job.operation === "encode" ? data.value ?? null : job.decoded ?? null);
        else if (data.phase === "next" && job.operation === "encode") nextBatch(job);
        else if (data.phase === "header" && job.operation === "decode" && data.value && typeof data.value === "object" && Array.isArray((data.value as { models?: unknown }).models)) {
          job.decoded = data.value as Job["decoded"];
          nextBatch(job);
        } else if (data.phase === "models" && job.operation === "decode" && job.decoded && Array.isArray(data.value)) {
          job.decoded.models.push(...data.value);
          nextBatch(job);
        } else finish(null);
      };
      worker.onerror = worker.onmessageerror = () => {
        // A CSP/asset/runtime failure will not keep retrying on every cell/page.
        unavailable = true;
        stopSupplyPlanJsonCodec();
      };
    } catch {
      unavailable = true;
      stopSupplyPlanJsonCodec();
      return;
    }
  }
  active = queue.shift()!;
  const job = active;
  let value = job.value;
  if (job.operation === "encode") {
    if (!value || typeof value !== "object" || !Array.isArray((value as { models?: unknown }).models)) { finish(null); return; }
    job.models = (value as { models: unknown[] }).models;
    value = { ...value, models: [] };
  }
  job.value = undefined;
  send({ id: job.id, operation: job.operation, value });
}

function request(operation: Job["operation"], value: unknown, timeoutMs: number): Promise<unknown | null> {
  if (unavailable || typeof Worker === "undefined" || queue.length + Number(!!active) >= 4) return Promise.resolve(null);
  if (idle) clearTimeout(idle);
  idle = undefined;
  return new Promise(resolve => {
    const id = ++sequence;
    const timer = setTimeout(() => {
      if (active?.id === id) stopSupplyPlanJsonCodec();
      else {
        queue = queue.filter(job => job.id !== id);
        resolve(null);
      }
    }, timeoutMs);
    queue.push({ id, operation, value, resolve, timer });
    pump();
  });
}

export async function encodeSupplyPlanSnapshot(value: unknown): Promise<Blob | null> {
  // Background save does not delay opening. Its UI-yielding batches may wait
  // behind a cold table render (observed >10s under CPU contention); allow
  // that render to finish rather than silently discard the persistent cache.
  // Reads keep a short deadline because the hook awaits them before the network.
  const result = await request("encode", value, 30_000);
  return result instanceof Blob ? result : null;
}

export function decodeSupplyPlanSnapshot(body: Blob): Promise<unknown | null> {
  return request("decode", body, 4_000);
}
